import every from "lodash/every";
import filter from "lodash/filter";
import sortBy from "lodash/sortBy";
import orderBy from "lodash/orderBy";
import uniq from "lodash/uniq";
import {
  productIsPurchasable,
  productSortPrice,
  StatusCodes
} from "./ProductUtils";

export function facetProducts(products, field) {
  const data = (products || [])
    .filter(product => field in product.specsIndexed)
    .map(product => product.specsIndexed[field].value);
  return uniq(data).reduce((map, value) => {
    map[value] = data.filter(v => v === value).length;
    return map;
  }, {});
}

export function facetProductsGroupedBy(products, field, groupByField) {
  const data = (products || [])
    .filter(product => field in product.specsIndexed)
    .map(product => ({
      value: product.specsIndexed[field].value,
      groupValue: product.specsIndexed[groupByField]?.value || "–"
    }));
  return uniq(data).reduce((map, item) => {
    if (map[item.groupValue] === undefined) {
      map[item.groupValue] = {};
    }
    map[item.groupValue][item.value] = data.filter(
      v => v.value === item.value && v.groupValue === item.groupValue
    ).length;
    return map;
  }, {});
}

/**
 * Filters are represented in the url as "Sortering(Rustik,Select)F%C3%A4rg(Brun)"
 */
export function decodeFilterQstring(qstring) {
  const parts = qstring.split(")");
  const filters = {};
  parts.forEach(f => {
    const [fieldStr, valueStr] = f.split("(");
    if (valueStr) {
      const field = decodeURIComponent(fieldStr);
      const values = valueStr.split(",").map(v => decodeURIComponent(v));
      filters[field] = values;
    }
  });
  return filters;
}

export function encodeFilterQstring(filters) {
  return Object.entries(filters)
    .map(
      ([field, values]) =>
        encodeURIComponent(field) +
        "(" +
        values.map(v => encodeURIComponent(v)).join(",") +
        ")"
    )
    .join("");
}

export function filterProducts(products, activeFilters) {
  if (!products || products.length === 0) {
    return [];
  }
  if (!activeFilters || Object.keys(activeFilters).length === 0) {
    return products;
  }
  return products.filter(product => {
    const matches = Object.entries(activeFilters).map(([filter, values]) => {
      return (
        filter in product.specsIndexed &&
        values.includes(product.specsIndexed[filter].value)
      );
    });
    return every(matches);
  });
}

export function filterProductsByHasSpec(products, spec) {
  if (!products || products.length === 0) {
    return [];
  }
  if (!spec) {
    return products;
  }
  return products.filter(product => {
    return spec in product.specsIndexed;
  });
}

export function filterProductsByAttr(products, attr) {
  return filter(products, p => p[attr]);
}

export function filterProductsByAttrValue(products, attr, value) {
  return filter(products, p =>
    Array.isArray(p[attr]) ? p[attr].includes(value) : p[attr] === value
  );
}

export function filterProductsByAttrValues(products, attr, values) {
  return values.length === 0
    ? []
    : filter(products, p => values.includes(p[attr]));
}

export function filterProductsBySkus(products, skus) {
  return skus.length === 0 ? [] : filter(products, p => skus.includes(p.sku));
}

export function filterProductsByIds(products, ids) {
  return ids.length === 0 ? [] : filter(products, p => ids.includes(p._id));
}

export function filterValidSkus(products, skus) {
  return filterProductsBySkus(products, skus).map(prod => prod.sku);
}

export function productSimpleSearch(products, queryStr) {
  if (!queryStr || queryStr.length <= 2) return [];
  const lq = queryStr.toLocaleLowerCase();
  return products
    .filter(p => {
      const name = p.name.toLocaleLowerCase();
      return name.includes(lq) || p.sku.startsWith(lq);
    })
    .sort((a, b) => {
      const aName = a.name.toLocaleLowerCase();
      const bName = b.name.toLocaleLowerCase();
      return (
        32 * (b.sku === lq) +
        16 * productIsPurchasable(b) +
        8 * b.sku.startsWith(lq) +
        4 * (bName === lq) +
        2 * bName.startsWith(lq) -
        32 * (a.sku === lq) -
        16 * productIsPurchasable(a) -
        8 * a.sku.startsWith(lq) -
        4 * (aName === lq) -
        2 * aName.startsWith(lq) +
        (aName < bName ? -1 : 1)
      );
    })
    .slice(0, 15);
}

function sortProductsBySpecs(products, field, valueOrder = {}) {
  return products.sort((a, b) => {
    const indexA = valueOrder[field]
      ? valueOrder[field].indexOf(a.specsIndexed[field]?.value)
      : 0;
    const indexB = valueOrder[field]
      ? valueOrder[field].indexOf(b.specsIndexed[field]?.value)
      : 0;
    //If value is not found in valueOrder, put it last
    const diff =
      (indexA > -1 ? indexA : Infinity) - (indexB > -1 ? indexB : Infinity);
    return diff !== 0 ? diff : a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  });
}

function extractCollection(products, filterFunc) {
  const matched = products.filter(filterFunc);
  const rest = products.filter(p => !matched.includes(p));
  return [matched, rest];
}

function sortProductsByRelevance(products) {
  const inStockLimit = 250;
  let upcomingProducts,
    newProducts,
    soldOut,
    withCampaign,
    rest,
    discontinuedProducts;
  [soldOut, rest] = extractCollection(
    products,
    p =>
      p.stock &&
      (p.stock.status === StatusCodes.soldOut ||
        p.stock.status === StatusCodes.outOfStock)
  );
  [discontinuedProducts, rest] = extractCollection(rest, p => p.discontinued);
  [withCampaign, rest] = extractCollection(rest, p => p.campaign_product);
  [upcomingProducts, rest] = extractCollection(rest, p => p.upcoming);
  [newProducts, rest] = extractCollection(rest, p => p.new_product);
  return [
    withCampaign,
    newProducts,
    rest,
    discontinuedProducts,
    upcomingProducts,
    soldOut
  ]
    .map(collection =>
      orderBy(
        collection,
        [
          p =>
            parseFloat(p.stock?.availableUnitStock) >= inStockLimit
              ? inStockLimit
              : parseFloat(p.stock?.availableUnitStock) ||
                parseInt(p.stock?.availableStock),
          "hasSampleInStock",
          "name"
        ],
        ["desc", "desc", "asc"]
      )
    )
    .flat(1);
}

const sortByAttributes = {
  relevance: 1,
  name: 2,
  price_asc: 3,
  price_desc: 4,
  grade: 5,
  color: 6
};

export const SORT_BY = { ...sortByAttributes };

export function sortProducts(
  products,
  sort = sortByAttributes.relevance,
  valueOrder = {}
) {
  switch (sort) {
    case sortByAttributes.name:
      return sortBy(products, ["name"]);
    case sortByAttributes.price_asc:
      return sortBy(products, [productSortPrice, "name"]);
    case sortByAttributes.price_desc:
      return sortBy(products, [productSortPrice, "name"]).reverse();
    case sortByAttributes.grade:
      return sortProductsBySpecs(products, "Grading", valueOrder);
    case sortByAttributes.color:
      return sortProductsBySpecs(products, "Staining", valueOrder);
    case sortByAttributes.relevance:
      return sortProductsByRelevance(products);
    default:
      return products;
  }
}

export function getRankedAccessories(products) {
  return Object.entries(
    products.reduce((counts, prod) => {
      prod.accessories.forEach(sku => {
        counts[sku] = (counts[sku] || 0) + 1;
      });
      return counts;
    }, {})
  )
    .sort(([skuA, countA], [skuB, countB]) => countB - countA)
    .map(([sku, count]) => sku);
}

export function filterProductsBySkuOrName(products, productFilters = []) {
  return productFilters.length === 0
    ? products
    : products.filter(({ sku, name }) => {
        const filterMatches = productFilters
          .map(
            filter =>
              sku.toLowerCase().startsWith(filter) ||
              (name && name.toLowerCase().indexOf(filter) > -1)
          )
          .filter(a => !!a).length;
        return filterMatches === productFilters.length;
      });
}
